import React, {
    useMemo
} from 'react';
import {
    Label
} from 'shared/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import ChipEditor from '../../primitives/ChipEditor';
import LongTextField from '../../primitives/LongTextField';

/**
 * TypedInputSection — typed_input shape (context_keys, propensity_keys,
 * intent.format / routing_mode, entity_schema). Drives both runtime
 * validation and the assistant-test panel's discovered fields.
 *
 * opcodes: assistant.opcodes[] — scanned for input_mapping references so
 * implied context/propensity keys appear as ghost chips the user can
 * promote to declared keys with one click.
 */
export default function TypedInputSection({
    a, onPatch, opcodes = []
}) {
    const ti = a.typed_input || {};
    const setTi = (updates) => onPatch({
        typed_input: {
            ...ti,
            ...updates
        }
    });
    const intent = ti.intent || {
        format: 'text',
        routing_mode: 'default_only'
    };

    // Scan all opcode input_mappings for context.<key> / propensity.<key> refs.
    const {
        impliedContext, impliedPropensity
    } = useMemo(() => {
        const ctx = new Map();
        const prop = new Map();
        for (const entry of opcodes) {
            const opcodeId = entry.opcode_id || entry.id || '?';
            for (const [inputName, target] of Object.entries(entry.input_mapping || {})) {
                const m = (target || '').match(/^(context|propensity)\.(.+)$/);
                if (!m) continue;
                const bucket = m[1] === 'context' ? ctx : prop;
                const key = m[2];
                if (!bucket.has(key)) {
                    bucket.set(key, `Implied by opcode ${opcodeId} → ${inputName}`);
                } else {
                    // append additional opcode references to tooltip
                    bucket.set(key, bucket.get(key) + `; ${opcodeId} → ${inputName}`);
                }
            }
        }
        return {
            impliedContext: Array.from(ctx.entries()).map(([value, tooltip]) => ({
                value,
                tooltip
            })),
            impliedPropensity: Array.from(prop.entries()).map(([value, tooltip]) => ({
                value,
                tooltip
            })),
        };
    }, [opcodes]);

    return (
        <div className="space-y-3">
            <div>
                <Label>Context Keys (soft validation)</Label>
                <ChipEditor
                    value={ti.context_keys || []}
                    onChange={(v) => setTi({
                        context_keys: v
                    })}
                    placeholder="e.g. language, customer_tier"
                    impliedChips={impliedContext}
                />
                {impliedContext.length > 0 && (
                    <p className="mt-1 text-[10.5px] text-muted-foreground">
                        Dashed chips are referenced by opcode input mappings — click to declare them explicitly.
                    </p>
                )}
            </div>
            <div>
                <Label>Propensity Keys (soft validation)</Label>
                <ChipEditor
                    value={ti.propensity_keys || []}
                    onChange={(v) => setTi({
                        propensity_keys: v
                    })}
                    placeholder="e.g. urgency, formality"
                    impliedChips={impliedPropensity}
                />
                {impliedPropensity.length > 0 && (
                    <p className="mt-1 text-[10.5px] text-muted-foreground">
                        Dashed chips are referenced by opcode input mappings — click to declare them explicitly.
                    </p>
                )}
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                    <Label>Intent format</Label>
                    <Select value={intent.format} onValueChange={(v) => setTi({
                        intent: {
                            ...intent,
                            format: v
                        }
                    })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="text">text</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Routing mode (enforced)</Label>
                    <Select value={intent.routing_mode} onValueChange={(v) => setTi({
                        intent: {
                            ...intent,
                            routing_mode: v
                        }
                    })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="intent_match">intent_match — match to opcode purpose</SelectItem>
                            <SelectItem value="default_only">default_only — always use default_opcode</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div>
                <Label>Entity Schema (JSON Schema)</Label>
                <LongTextField
                    label="Entity Schema (JSON Schema)"
                    value={ti.entity_schema ? JSON.stringify(ti.entity_schema, null, 2) : ''}
                    onChange={(text) => {
                        if (!text || !text.trim()) {
                            setTi({
                                entity_schema: {}
                            });
                            return;
                        }
                        try {
                            setTi({
                                entity_schema: JSON.parse(text)
                            });
                        } catch {
                            /* keep last valid; error shown on save */
                        }
                    }}
                    placeholder='{"type":"object","properties":{}}'
                    helpText="Per-request validated. Mind raises InputError on mismatch."
                />
            </div>
        </div>
    );
}
